import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BiSearch, BiPlus, BiShow } from "react-icons/bi";

import { getInvoiceList, invoiceV2Selector } from "./invoiceV2Slice";
import { Colors } from "../../../assets/Colors";

const STATUS_CONFIG = {
  draft:     { label: "Draft",     bg: "bg-gray-100",   text: "text-gray-600",   dot: "bg-gray-400"   },
  sent:      { label: "Terkirim",  bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500"   },
  paid:      { label: "Lunas",     bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500"  },
  cancelled: { label: "Dibatalkan",bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500"    },
};

function fmt(n) {
  if (!n && n !== 0) return "0";
  return new Intl.NumberFormat("id-ID").format(n);
}

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />{c.label}
    </span>
  );
}

export default function InvoiceListV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const invV2 = useSelector(invoiceV2Selector);
  const [input, setInput] = useState("");

  useEffect(() => { dispatch(getInvoiceList("")); }, []); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => dispatch(getInvoiceList(input)), 400);
    return () => clearTimeout(t);
  }, [input]); // eslint-disable-line

  const list = invV2.list ?? [];

  return (
    <div className="md:ml-64">
      <div style={{ background: Colors.primary }} className="pt-14 pb-20 px-8" />
      <div className="px-4 md:px-8 -mt-16">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">

          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-bold text-gray-800">Daftar Invoice</h2>
            <button onClick={() => navigate("/invoice-generate-v2")}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold"
              style={{ background: Colors.primary }}>
              <BiPlus className="text-lg" /> Buat Invoice
            </button>
          </div>

          <div className="px-6 py-3 border-b bg-gray-50">
            <div className="relative max-w-sm">
              <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input type="text" value={input} onChange={e => setInput(e.target.value)}
                placeholder="Cari no. invoice, QO, atau klien…"
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none" />
            </div>
          </div>

          <div className="overflow-x-auto">
            {invV2.isLoading ? (
              <div className="py-16 text-center text-gray-400 text-sm">Memuat…</div>
            ) : list.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <span className="text-4xl mb-3">🧾</span>
                <p className="text-sm">Belum ada invoice</p>
                <button onClick={() => navigate("/invoice-generate-v2")}
                  className="mt-3 text-xs text-orange-500 underline">Buat invoice pertama</button>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-5 py-3 font-semibold text-gray-600">No. Invoice</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">No. QO</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">Klien</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">CQ</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">Tgl Invoice</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">Jatuh Tempo</th>
                    <th className="px-5 py-3 font-semibold text-gray-600 text-right">Total</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">Status</th>
                    <th className="px-5 py-3 font-semibold text-gray-600 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {list.map(row => (
                    <tr key={row.InvID} className="hover:bg-orange-50 cursor-pointer"
                      onClick={() => navigate("/invoice-detail-v2", { state: { InvNo: row.InvNo } })}>
                      <td className="px-5 py-3.5 font-mono text-xs text-gray-700">{row.InvNo}</td>
                      <td className="px-5 py-3.5 font-mono text-xs text-gray-600">{row.QoNo}</td>
                      <td className="px-5 py-3.5 text-gray-700 max-w-[180px] truncate">{row.KlienName}</td>
                      <td className="px-5 py-3.5 text-gray-700 max-w-[200px] truncate" title={row.CQName}>
                        {row.CQName || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-5 py-3.5 text-gray-700">{row.InvoiceDate}</td>
                      <td className="px-5 py-3.5 text-gray-700">{row.DueDate}</td>
                      <td className="px-5 py-3.5 text-right font-medium text-gray-800">Rp {fmt(row.TotalAmount)}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={row.Status} /></td>
                      <td className="px-5 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <button onClick={() => navigate("/invoice-detail-v2", { state: { InvNo: row.InvNo } })}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 border-gray-300 hover:bg-gray-50">
                          <BiShow className="text-sm" /> Detail
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!invV2.isLoading && list.length > 0 && (
            <div className="px-6 py-3 border-t bg-gray-50 text-xs text-gray-500">
              Total <strong>{list.length}</strong> invoice
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
